import { Categorizable, CategoryModel, ClickableCategoryModel } from 'src/types/category.model';
import BWDataService from './bw-data.service';
import _ from 'lodash';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TransactionModel } from 'src/types/transaction.model';
import { CustomSubcategoryModalStateModel } from 'src/types/custom-subcategory-modal-state.model';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { ruleOrTransaction } from 'src/types/ruleOrTransaction';
import { TransactionType } from 'src/types/transaction-type.enum';
import { getIcon } from 'src/utils/category.utils';

export enum Categories {
    UncategorizedGroupId = '5d31aa47-fb42-38e5-b698-d85aa890f120'
}

export default class CategoryService extends BWDataService {
    public static async createCategory(category: CategoryModel): Promise<CategoryModel> {
        if (!category || !category.name) return;
        return await this.postCategory(category);
    }

    private static async postCategory(category: CategoryModel): Promise<CategoryModel> {
        const payload: CategoryModel = {
            name: category.name,
            parentId: category.parentId
        };

        return await this.post('/categories', payload);
    }

    /** get the list of categories for the current user: Travel, Business, Entertainment, etc */
    public static async getCategories(transactionType?: TransactionType): Promise<CategoryModel[]> {
        const data: CategoryModel[] = await this.get(
            `/categories${transactionType ? '?transactionType=' + encodeURIComponent(transactionType) : ''}`
        );
        return this.sortCategoriesByName(data);
    }

    public static sortCategoriesByName(categories: CategoryModel[]): CategoryModel[] {
        return _.sortBy(categories, 'name');
    }

    public static async deleteCategory(id: string): Promise<number> {
        return await this.delete('/categories/' + id);
    }

    /** Nest into a list of categories, with subcategory arrays of key "subcategories"  */
    public static nestCategories(
        categories: CategoryModel[]
    ): Array<CategoryModel & { subcategories?: CategoryModel[] }> {
        // sorting dictionary to do this in linear time
        const d: { [key: string]: CategoryModel & { subcategories?: CategoryModel[] } } = {};

        // nest all categories and subcategories
        for (const e of categories) {
            if (!e.parentId) {
                if (d[e.id]) d[e.id] = { ...d[e.id], ...e };
                else d[e.id] = { ...e, subcategories: [] };
            } else {
                if (d[e.parentId]) d[e.parentId].subcategories.push({ ...e });
                else
                    d[e.parentId] = { subcategories: [{ ...e }] } as CategoryModel & {
                        subcategories?: CategoryModel[];
                    };
            }
        }

        // convert sorting dictinary into array
        let a: Array<CategoryModel & { subcategories?: CategoryModel[] }> = Object.values(d);

        // sort categories alphabetically
        a = _.sortBy(a, ['name']);

        // sort subcategories alphabetically
        for (const e of a) e.subcategories = _.sortBy(e.subcategories, ['name']);

        return a;
    }

    /** Flattened list sorting alphabetically for subcategories within categories  */
    public static sortCategories(categories: CategoryModel[]): CategoryModel[] {
        let a: CategoryModel[] = [];
        const nested = CategoryService.nestCategories(categories);
        for (const e of nested) {
            a.push({ ...e });
            a = a.concat(e.subcategories);
        }
        return a;
    }

    /** Add icons to categories array  */
    public static addIcons(categories: CategoryModel[]): CategoryModel[] {
        for (const e of categories) if (!e.parentId) e.icon = getIcon(e.name);
        return categories;
    }

    /** Get category options with icons for expense rules and transactions */
    public static getCategoryOptions<T extends ruleOrTransaction>(
        categories: CategoryModel[],
        value: T,
        handleOnClick: ({ value, parentCategoryId }: CustomSubcategoryModalStateModel<T>) => void
    ): ClickableCategoryModel[] {
        const categoryOptions: ClickableCategoryModel[] = [];
        const categoriesWithIcons: CategoryModel[] = CategoryService.addIcons(
            CategoryService.sortCategories(categories)
        );

        const uncategorizedCategory: CategoryModel = categoriesWithIcons.find(
            (cat) => cat.id === Categories.UncategorizedGroupId
        );
        categoriesWithIcons?.forEach((e, i) => {
            if (!e.parentId && i !== 0 && categoriesWithIcons[i - 1].id !== Categories.UncategorizedGroupId) {
                categoryOptions.push({
                    name: LiteralsService.getLiteral('CustomSubcategoryModal.customSubcategoryOption'),
                    onClick: () => {
                        handleOnClick({
                            value,
                            parentCategoryId: categoriesWithIcons[i - 1].parentId || categoriesWithIcons[i - 1].id
                        });
                    }
                });
            }
            if (e.id === Categories.UncategorizedGroupId) {
                return;
            }
            categoryOptions.push(e);
            if (i === categoriesWithIcons.length - 1 && e.id !== Categories.UncategorizedGroupId) {
                categoryOptions.push({
                    name: LiteralsService.getLiteral('CustomSubcategoryModal.customSubcategoryOption'),
                    onClick: () => {
                        handleOnClick({
                            value,
                            parentCategoryId: categoriesWithIcons[i].parentId || categoriesWithIcons[i].id
                        });
                    }
                });
            }
        });
        if ((value as TransactionModel).groupUid === Categories.UncategorizedGroupId) {
            categoryOptions.push(uncategorizedCategory);
        }
        return categoryOptions;
    }

    public static getCategoryOptionsWithNoCustomSubcategory(categories: CategoryModel[]): CategoryModel[] {
        const categoryOptions: CategoryModel[] = CategoryService.addIcons(CategoryService.sortCategories(categories));
        const uncategorizedCategory: CategoryModel = categoryOptions.find(
            (cat) => cat.id === Categories.UncategorizedGroupId
        );
        return categoryOptions
            .filter((cat) => cat.id !== Categories.UncategorizedGroupId)
            .concat(uncategorizedCategory);
    }

    public static groupByCategory(
        items: Categorizable[],
        categories: CategoryModel[],
        sortedBy: _.ListIteratee<Categorizable>
    ): Categorizable[][] {
        if (items?.length === 0 || categories?.length === 0) return [];
        const parentItems = _.sortBy(
            items?.filter((item) => {
                const category = categories?.find((cat) => cat.id === item.groupId);
                return item.groupId !== BUDGET_OVERALL_ID && !category?.parentId;
            }),
            sortedBy
        );
        return parentItems.map((parentItem) => {
            let childItems = _.sortBy(
                items?.filter((item) => {
                    const category = categories?.find((cat) => cat.id === item.groupId);
                    return category && category.parentId === parentItem.groupId;
                }),
                sortedBy
            );
            childItems = childItems.map((childItem) => {
                // save the parent groupId on the child so that we don't have to iterate through
                // all the categories to find this id again
                return { ...childItem, parentGroupId: parentItem.groupId };
            });
            return [parentItem, ...childItems];
        });
    }
}
